import type { IntlShape } from 'react-intl'

import type { Job } from './+data'

export type CompanyStats = {
  founded: string
  funding: string
  teamSize: string
  cities: string
}

export type WorkCulturePillar = {
  title: string
  description: string
  icon?: string
  color: string
}

export const SECTION_WHAT_WE_OFFER = `
- Challenges on a product you'll use daily
- Competitive salary and equity
- Remote-only environment built from day zero of the company
- 2 Team off-sites per year
- Co-working office rental or home office budget
- 2 months minimum of parental leave
- 7 weeks of holidays per year
`

export const COMPANY_STATS: CompanyStats = {
  founded: '2016',
  funding: '14M',
  teamSize: '20',
  cities: '16',
}

export const getWorkCulturePillars = (intl: IntlShape): WorkCulturePillar[] => [
  {
    color: 'white',
    icon: 'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/ebcf0133f5bfdd6fd4fa4ca6b2102614502183ff-500x318.webp',
    title: intl.formatMessage({
      id: 'jobs.culture.pillar.written.title',
      defaultMessage: 'Written culture',
    }),
    description: intl.formatMessage(
      {
        id: 'jobs.culture.pillar.written.description',
        defaultMessage:
          'We invest time and care in writing thoughtful, long-form content and documenting our work.{br}{br}This allows us to have deeper discussions on important topics, reduce unnecessary meetings, and build lasting team knowledge.',
      },
      { br: '\n' }
    ),
  },
  {
    color: 'dust',
    icon: 'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/069cbce495a55b3d5c9450eedc49faf6993a0a0c-500x266.webp',
    title: intl.formatMessage({
      id: 'jobs.culture.pillar.transparency.title',
      defaultMessage: 'Transparency',
    }),
    description: intl.formatMessage(
      {
        id: 'jobs.culture.pillar.transparency.description',
        defaultMessage:
          "By default, we share decisions, context, and knowledge openly in Slite.{br}{br}Everyone can understand not only what we're building, but why. That shared context reduces friction, helps people make better decisions, and gives every Sliter the confidence to contribute.",
      },
      { br: '\n' }
    ),
  },
  {
    color: 'primary',
    icon: 'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/614cd4334670ce436eaee6706b10be25aa3950fd-620x620.webp',
    title: intl.formatMessage({
      id: 'jobs.culture.pillar.async.title',
      defaultMessage: 'Async work, real interactions',
    }),
    description: intl.formatMessage(
      {
        id: 'jobs.culture.pillar.async.description',
        defaultMessage:
          "We see async work as a way to save time, enjoy flexible schedules, and take ownership of how we work.{br}{br}At the same time, we're human. We believe in meeting face-to-face—or over video—for 1:1s, brainstorming sessions, and the conversations that build strong, lasting relationships.",
      },
      { br: '\n' }
    ),
  },
  {
    color: 'pink-moon',
    icon: 'https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/8eaa6ad1d3e65fe1fb2b6b3ec694b15fb330f2a1-500x500.webp',
    title: intl.formatMessage({
      id: 'jobs.culture.pillar.remote.title',
      defaultMessage: 'Remote',
    }),
    description: intl.formatMessage(
      {
        id: 'jobs.culture.pillar.remote.description',
        defaultMessage:
          "We've been a remote-first company since 2017, and we've never looked back.{br}{br}We embrace remote work through our strong written culture, while getting together in person every 2–4 months to strengthen relationships, collaborate, and spark new ideas.",
      },
      { br: '\n' }
    ),
  },
]

export const getCategoryTitle = (intl: IntlShape, category: string): string => {
  const categoryMap: Record<string, string> = {
    engineering: intl.formatMessage({
      id: 'jobs.category.engineering',
      defaultMessage: 'Engineering',
    }),
    marketing: intl.formatMessage({
      id: 'jobs.category.marketing',
      defaultMessage: 'Marketing',
    }),
    sales: intl.formatMessage({
      id: 'jobs.category.sales',
      defaultMessage: 'Sales',
    }),
    design: intl.formatMessage({
      id: 'jobs.category.design',
      defaultMessage: 'Design',
    }),
    product: intl.formatMessage({
      id: 'jobs.category.product',
      defaultMessage: 'Product',
    }),
    operations: intl.formatMessage({
      id: 'jobs.category.operations',
      defaultMessage: 'Operations',
    }),
    'customer-success': intl.formatMessage({
      id: 'jobs.category.customerSuccess',
      defaultMessage: 'Customer Success',
    }),
    finance: intl.formatMessage({
      id: 'jobs.category.finance',
      defaultMessage: 'Finance',
    }),
    'human-resources': intl.formatMessage({
      id: 'jobs.category.humanResources',
      defaultMessage: 'Human Resources',
    }),
  }

  return categoryMap[category] || category
}

export const groupJobsByCategory = (
  jobs: Job[]
): { category: string; jobs: Job[] }[] => {
  const grouped = jobs.reduce(
    (acc, job) => {
      if (!acc[job.category]) {
        acc[job.category] = []
      }
      acc[job.category].push(job)
      return acc
    },
    {} as Record<string, Job[]>
  )

  return Object.entries(grouped).map(([category, jobs]) => ({
    category,
    jobs,
  }))
}
